/**
 * Klantkaart v10 (motor 7, golf 2 sessie E; Brain grote-ronde/75 keuzes 11
 * tot en met 18, mock werkplek v10). Eén kaart voor het rechtervlak van de
 * zaak en voor de pagina Klanten (`/klanten/:id`):
 *
 * - kop: initialen, naam, adres in twee regels, AVG-knop (bestaande
 *   akkoord-kaart, alleen beheerder);
 * - knoptegels met de waarde erin: Mail (opent een nieuw bericht per mail),
 *   Bel (tel-link), WhatsApp (opent een WhatsApp-gesprek in de werkplek),
 *   Lightspeed (klant-id, opent de klant in een nieuw tabblad);
 * - cijfers 2x2 uit de backend: klantwaarde, bestellingen gemiddeld (bedrag
 *   en interval), klant sinds plus duur, volgende bestelling in oranje;
 * - tabs die de rest van de kaart vullen: Dier, Bestelling, Retour,
 *   Contact, Reviews. De laatst gekozen tab blijft per sessie staan.
 *   Sessie 141-E: tellers op Dier, Retour, Contact en Reviews (zoals de
 *   mock), uit dezelfde cache als de tabs; geen teller tijdens laden of bij
 *   nul. Een open belofte staat als woord in de naam van de tab Contact.
 *
 * Geen Geld-knop, geen Account-functie (keuze 11). Wat in de kop of de
 * cijfers staat, komt niet nog eens in een tab (keuze 17). Wat we niet
 * weten staat er als "onbekend", nooit als verzonnen getal.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { klantenApi } from "@/lib/api";
import { useAuthStore } from "@/lib/auth-store";

import { AvgVerwijderen, hoofdEmail, MogelijkDezelfdeKlant, nummers } from "../../Klanten/kaart";
import type { KlantKaart } from "../../Klanten/types";
import { reviewsSleutel } from "../../Klanten/kaart/hulp";
import { retourLink } from "../../../Admin/retourLink";
import { euro, veiligeLink } from "../opmaak";
import { kaart10Api, kaart10Sleutels } from "./api";
import {
  bewaarTab,
  bewaardeTab,
  contactAantal,
  duurWoord,
  maandJaarKort,
  TABS,
  volgendeTegel,
  type Tab,
} from "./hulp";
import { TabBestelling } from "./TabBestelling";
import { TabContact } from "./TabContact";
import { TabDier } from "./TabDier";
import { TabRetour } from "./TabRetour";
import { TabReviews } from "./TabReviews";
import "./klantkaart.css";

export interface KaartActies {
  /** Nieuw bericht per mail of WhatsApp; er wordt nooit iets verstuurd. */
  nieuw: (kanaal: "mail" | "whatsapp") => void;
  bezig?: boolean;
}

function telLink(nummer: string): string {
  return `tel:${nummer.replace(/[^\d+]/g, "")}`;
}

function TegelIcoon({ soort }: { soort: "mail" | "tel" | "wa" | "ls" }) {
  const paden: Record<typeof soort, ReactNode> = {
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
      </>
    ),
    tel: (
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
    ),
    wa: <path d="M3 21l1.6-4.7A8.5 8.5 0 1 1 8 19.5Z" />,
    ls: (
      <>
        <path d="M14 4h6v6" />
        <path d="M20 4 10 14" />
        <path d="M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6" />
      </>
    ),
  };
  return (
    <span className={`ic ${soort}`}>
      <svg className="kk10-i" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        {paden[soort]}
      </svg>
    </span>
  );
}

/** Zo lang blijven de vooraf opgehaalde tabs vers (sessie 161). */
const VERS_MS = 60_000;

/**
 * Sessie 141-E: de tellers op de tabs Retour, Contact en Reviews. Leest uit
 * dezelfde cache als de tabs (en de prefetch van de kaart): geen extra
 * verzoeken. Tijdens laden, bij een fout of bij nul: geen teller.
 */
function useTabTellers(klantId: string): Partial<Record<Tab, string>> {
  const retouren = useQuery({
    queryKey: kaart10Sleutels.retouren(klantId),
    queryFn: () => kaart10Api.retouren(klantId),
    staleTime: VERS_MS,
  }).data;
  const gesprekken = useQuery({
    queryKey: kaart10Sleutels.gesprekken(klantId),
    queryFn: () => kaart10Api.gesprekken(klantId),
    staleTime: VERS_MS,
  }).data;
  const reviews = useQuery({
    queryKey: reviewsSleutel(klantId),
    queryFn: () => klantenApi.reviews(klantId),
    staleTime: 5 * VERS_MS,
  }).data;
  const tellers: Partial<Record<Tab, string>> = {};
  if (retouren && retouren.aantal > 0) tellers.retour = String(retouren.aantal);
  const contact = contactAantal(gesprekken);
  if (contact) tellers.contact = contact;
  if (reviews && reviews.length > 0) tellers.reviews = String(reviews.length);
  return tellers;
}

function Waarde({ label, waarde }: { label: string; waarde: string | null }) {
  return (
    <span className="tk">
      <span className="w">{label}</span>
      <span className={`v${waarde ? "" : " onbekend"}`}>{waarde ?? "onbekend"}</span>
    </span>
  );
}

/**
 * Sessie 161: het mailadres volledig leesbaar, geen ellips. Het mag
 * afbreken voor de @ (en desnoods elders), in een iets kleinere letter.
 */
function MailWaarde({ email }: { email: string | null }) {
  if (!email) return <Waarde label="Mail" waarde={null} />;
  const at = email.indexOf("@");
  return (
    <span className="tk">
      <span className="w">Mail</span>
      <span className="v mail" data-testid="mail-waarde">
        {at > 0 ? (
          <>
            {email.slice(0, at)}
            <wbr />
            {email.slice(at)}
          </>
        ) : (
          email
        )}
      </span>
    </span>
  );
}

function Knoptegels({
  kaart,
  acties,
  belTerugval,
}: {
  kaart: KlantKaart;
  acties: KaartActies;
  belTerugval?: string | null;
}) {
  const email = hoofdEmail(kaart);
  const { bel: eigenBel, mobiel } = nummers(kaart);
  // Een belzaak zonder nummer op de klant: het nummer van het gesprek zelf.
  const bel = eigenBel || belTerugval || null;
  const lsUrl = veiligeLink(kaart.kop.lightspeed_url);
  const account =
    kaart.kop.lightspeed_accounts.find((a) => a.status === "eigen" && a.lightspeed_id) ??
    kaart.kop.lightspeed_accounts.find((a) => a.lightspeed_id) ??
    null;
  const lsId = account?.lightspeed_id ?? null;
  return (
    <div className="kk10-tegels2" data-testid="knoptegels">
      <button
        type="button"
        className="kt"
        disabled={!email || acties.bezig}
        title={email ? `Nieuw bericht per mail aan ${email}` : "Geen e-mailadres bekend"}
        onClick={() => acties.nieuw("mail")}
      >
        <TegelIcoon soort="mail" />
        <MailWaarde email={email} />
      </button>
      {bel ? (
        <a className="kt" href={telLink(bel)} title={`Bel ${bel}`}>
          <TegelIcoon soort="tel" />
          <Waarde label="Bel" waarde={bel} />
        </a>
      ) : (
        <span className="kt uit" aria-disabled="true" title="Geen nummer bekend">
          <TegelIcoon soort="tel" />
          <Waarde label="Bel" waarde={null} />
        </span>
      )}
      <button
        type="button"
        className="kt"
        disabled={!mobiel || acties.bezig}
        title={mobiel ? `WhatsApp-gesprek met ${mobiel}` : "Geen mobiel nummer bekend"}
        onClick={() => acties.nieuw("whatsapp")}
      >
        <TegelIcoon soort="wa" />
        <Waarde label="WhatsApp" waarde={mobiel} />
      </button>
      {lsUrl ? (
        <a
          className="kt ls"
          href={lsUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Klant openen in Lightspeed (nieuw tabblad)"
        >
          <TegelIcoon soort="ls" />
          <Waarde label="Lightspeed" waarde={lsId ? `klant ${lsId}` : "klant"} />
        </a>
      ) : (
        <span className="kt ls uit" aria-disabled="true" title="Geen Lightspeed-klant bekend">
          <TegelIcoon soort="ls" />
          <Waarde label="Lightspeed" waarde={lsId ? `klant ${lsId}` : null} />
        </span>
      )}
    </div>
  );
}

/** Sessie 161: "nieuw", of de datum met de duur ("mrt 2024 · 2,5 jaar"). */
function klantSinds(sinds: string | null | undefined, duur: string | null): string {
  if (!sinds) return "onbekend";
  if (duur === "nieuw") return "nieuw";
  return [maandJaarKort(sinds), duur].filter(Boolean).join(" · ");
}

export function Cijfers10({ kaart }: { kaart: KlantKaart }) {
  const c = kaart.cijfers;
  const duur = duurWoord(c.klant_sinds_maanden);
  const v = c.volgende ?? null;
  const gemiddeld = c.gemiddeld_cents !== null ? `gem. ${euro(c.gemiddeld_cents)}` : null;
  const interval = c.interval_dagen ? `elke ${c.interval_dagen} dagen` : null;
  const volgende = volgendeTegel(v, c.aantal_bestellingen);
  return (
    <div className="kk10-cijfers" data-testid="cijfers10">
      <div>
        <span className="w">Klantwaarde</span>
        <span className="n num">{euro(c.klantwaarde_cents)}</span>
      </div>
      <div>
        <span className="w">{["Bestellingen", gemiddeld].filter(Boolean).join(", ")}</span>
        <span className="n num">
          {[String(c.aantal_bestellingen), interval].filter(Boolean).join(" · ")}
        </span>
      </div>
      <div>
        <span className="w">Klant sinds</span>
        <span className={`n num${c.klant_sinds ? "" : " onbekend"}`} data-testid="klant-sinds">
          {klantSinds(c.klant_sinds, duur)}
        </span>
      </div>
      <div
        className={
          volgende.stand === "toekomst" ? "doe" : volgende.stand === "verleden" ? "verleden" : ""
        }
        title={v?.uitleg}
        data-testid="volgende"
        data-soort={v?.soort ?? "onbekend"}
        data-stand={volgende.stand}
      >
        <span className="w">{volgende.label}</span>
        <span className={`n num${volgende.stand === "onbekend" ? " onbekend" : ""}`}>
          {volgende.waarde}
        </span>
        {volgende.extra ? (
          <span className="x" data-testid="volgende-extra">
            {volgende.extra}
          </span>
        ) : null}
        <span className="sr-only">{v?.uitleg}</span>
      </div>
    </div>
  );
}

export function KlantkaartV10({
  kaart,
  acties,
  naamLink = false,
  onderregel,
  kopRechts,
  tussen,
  contactExtra,
  onGewist,
  onCopiloot,
  avgKnop = true,
  belTerugval,
  tabOnthouden = true,
}: {
  kaart: KlantKaart;
  acties: KaartActies;
  /** De naam klikt door naar `/klanten/{id}` (in de zaak). */
  naamLink?: boolean;
  /** Kleine regel onder het adres, bv. "voorstel op e-mailadres". */
  onderregel?: string | null;
  /** Rechts in de kop, zoals het kruisje op de iPhone. */
  kopRechts?: ReactNode;
  /** Onder de tegels: koppelvoorstel en foutmeldingen van de zaak. */
  tussen?: ReactNode;
  /** In de tab Contact: "niet deze klant" bij een gekoppelde zaak. */
  contactExtra?: ReactNode;
  /** Na AVG-wissen. */
  onGewist?: () => void;
  /** Na "Op de kaart" bij Uit gesprekken: op de iPhone de copiloot openen. */
  onCopiloot?: () => void;
  /** Uit bij een koppelvoorstel: AVG alleen op een echt gekoppelde klant. */
  avgKnop?: boolean;
  /** Belnummer van de zaak als de klant zelf geen nummer heeft. */
  belTerugval?: string | null;
  /**
   * Sessie 161: in de werkplek begint elke zaak op de standaardtab (uit);
   * op de klantpagina blijft de laatst gekozen tab per sessie staan (aan).
   */
  tabOnthouden?: boolean;
}) {
  const [tab, setTab] = useState<Tab>(() => (tabOnthouden ? bewaardeTab() : TABS[0].id));
  const [avg, setAvg] = useState(false);
  const beheerder = useAuthStore((s) => s.gebruiker?.rol === "admin");
  const id = useId();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const kies = (t: Tab) => {
    setTab(t);
    if (tabOnthouden) bewaarTab(t);
  };
  // Sessie 161: Bestelling, Retour en Reviews alvast ophalen zodra de kaart
  // opent, zodat een tab niet eerst seconden "worden geladen" toont.
  const queryClient = useQueryClient();
  const klantId = kaart.id;
  useEffect(() => {
    const vers = VERS_MS;
    void queryClient.prefetchQuery({
      queryKey: kaart10Sleutels.bestellingen(klantId),
      queryFn: () => kaart10Api.bestellingen(klantId),
      staleTime: vers,
    });
    void queryClient.prefetchQuery({
      queryKey: kaart10Sleutels.retouren(klantId),
      queryFn: () => kaart10Api.retouren(klantId),
      staleTime: vers,
    });
    void queryClient.prefetchQuery({
      queryKey: reviewsSleutel(klantId),
      queryFn: () => klantenApi.reviews(klantId),
      staleTime: 5 * vers,
    });
    // Sessie 141-E: ook de contacten, voor de teller en "Eerdere contacten".
    void queryClient.prefetchQuery({
      queryKey: kaart10Sleutels.gesprekken(klantId),
      queryFn: () => kaart10Api.gesprekken(klantId),
      staleTime: vers,
    });
  }, [queryClient, klantId]);
  const tellers = useTabTellers(klantId);
  // Pijltjes, Home en End wisselen de tab (WAI-ARIA tabs, automatische keuze).
  const toets = (e: KeyboardEvent<HTMLButtonElement>) => {
    const nu = TABS.findIndex((t) => t.id === tab);
    let volgende = -1;
    if (e.key === "ArrowRight") volgende = (nu + 1) % TABS.length;
    else if (e.key === "ArrowLeft") volgende = (nu - 1 + TABS.length) % TABS.length;
    else if (e.key === "Home") volgende = 0;
    else if (e.key === "End") volgende = TABS.length - 1;
    if (volgende < 0) return;
    e.preventDefault();
    const doel = TABS[volgende].id;
    kies(doel);
    tabRefs.current[doel]?.focus();
  };
  const { adres_regel1, adres_regel2 } = kaart.kop;
  const levend = kaart.dieren.filter((d) => !d.is_overleden).length;
  if (levend > 0) tellers.dier = String(levend);
  const beloftes = kaart.beloftes.length;

  return (
    <div className="kk10" data-testid="klantkaart10">
      <div className="kk10-kop">
        <div className="r1">
          <span className="av" aria-hidden="true">
            {kaart.initialen}
          </span>
          <div className="wie">
            <h2 className="naam">
              {naamLink ? (
                <Link to={`/klanten/${kaart.id}`} title="Naar de klantkaart">
                  {kaart.naam}
                </Link>
              ) : (
                kaart.naam
              )}
            </h2>
            <span className="adres" data-testid="kop-adres">
              {adres_regel1 || adres_regel2 ? (
                <>
                  {adres_regel1 ? <span>{adres_regel1}</span> : null}
                  {adres_regel2 ? <span>{adres_regel2}</span> : null}
                </>
              ) : (
                <span className="onbekend">adres onbekend</span>
              )}
            </span>
            {onderregel ? <small className="onder">{onderregel}</small> : null}
          </div>
          {/* Sessie 171: vaste knop naar het retourformulier, met het
              mailadres ingevuld en de laatste bestellingen om te kiezen. */}
          <Link
            className="kk10-retourknop"
            to={retourLink({ mailadres: hoofdEmail(kaart), klant: kaart.id })}
            data-testid="klant-retour-knop"
            title="Retour aanmaken voor deze klant"
          >
            Retour
          </Link>
          {beheerder && avgKnop ? (
            <button
              type="button"
              className="avg"
              title="AVG: klant verwijderen (met akkoord-kaart)"
              onClick={() => setAvg(true)}
            >
              AVG
            </button>
          ) : null}
          {kopRechts}
        </div>
        <Knoptegels kaart={kaart} acties={acties} belTerugval={belTerugval} />
      </div>
      {tussen}
      {avg && avgKnop ? (
        <AvgVerwijderen
          key={kaart.id}
          klantId={kaart.id}
          onSluit={() => setAvg(false)}
          onGewist={() => {
            setAvg(false);
            onGewist?.();
          }}
        />
      ) : null}
      <Cijfers10 kaart={kaart} />
      {/* Sessie 186: stond alleen in de tab Contact en bleef daardoor
          onzichtbaar; nu altijd boven de tabs zodra er een voorstel is. */}
      <MogelijkDezelfdeKlant
        klantId={kaart.id}
        accounts={kaart.kop.lightspeed_accounts}
        className="kk10-mogelijk"
      />
      <div className="kk10-tabs" role="tablist" aria-label="Klantkaart">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`${id}-tab-${t.id}`}
            ref={(el) => {
              tabRefs.current[t.id] = el;
            }}
            aria-selected={tab === t.id}
            aria-controls={`${id}-paneel`}
            tabIndex={tab === t.id ? 0 : -1}
            className={tab === t.id ? "on" : ""}
            onClick={() => kies(t.id)}
            onKeyDown={toets}
          >
            {t.label}
            {tellers[t.id] ? <span className="tel">{tellers[t.id]}</span> : null}
            {/* Een open belofte als woord in de naam van de tab, nooit alleen
                kleur; de belofte zelf staat bovenaan in tab Contact. */}
            {t.id === "contact" && beloftes > 0 ? (
              <span className="sr-only" data-testid="tab-belofte">
                {`, ${beloftes} open ${beloftes === 1 ? "belofte" : "beloftes"}`}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div
        className="kk10-scrol"
        role="tabpanel"
        id={`${id}-paneel`}
        aria-labelledby={`${id}-tab-${tab}`}
      >
        {tab === "dier" ? <TabDier kaart={kaart} onCopiloot={onCopiloot} /> : null}
        {tab === "bestelling" ? (
          <TabBestelling klantId={kaart.id} mailadres={hoofdEmail(kaart)} />
        ) : null}
        {tab === "retour" ? <TabRetour kaart={kaart} /> : null}
        {tab === "contact" ? <TabContact kaart={kaart}>{contactExtra}</TabContact> : null}
        {tab === "reviews" ? <TabReviews klantId={kaart.id} /> : null}
      </div>
    </div>
  );
}
